/**
 * LeatherDeleteTool — the leather "Delete" method (#1000, epic #781): point at
 * a part, cutout or seam and click to remove it.
 *
 * A thin shell, like `LeatherMirrorTool` / `LeatherRoundTool`: it OWNS a
 * `LeatherDeleteController` and drives it by composition, supplying only the
 * editor plumbing. The gesture, the hover highlight and the commit live in the
 * controller.
 *
 * Mode-independent, like Mirror and Round — you delete an object, not a mode —
 * so `app.ts` hides the Leather / Cutout / Stitch row while it's active.
 */
import type paper from "paper";
import { ToolBase } from "./ToolBase";
import type { PointerSample } from "../canvas/PointerInput";
import type { Scene, SceneSelection } from "../canvas/Scene";
import type { Project } from "../model/Project";
import { deleteSelection } from "../model/deleteSelection";
import { setCanvasCursor } from "../canvas/canvasCursor";
import {
  LeatherDeleteController,
  type LeatherDeleteHost,
} from "./leatherDeleteController";

export class LeatherDeleteTool extends ToolBase implements LeatherDeleteHost {
  id = "leatherDelete";
  label = "Delete";
  // A plain pointer: this picks an existing object rather than placing
  // anything, matching Mirror and Round.
  cursor = "default";

  private del = new LeatherDeleteController(this);

  // --- LeatherDeleteHost ---------------------------------------------------

  getScope(): paper.PaperScope {
    return this.ctx.scene.getPaperScope();
  }

  getScene(): Scene {
    return this.ctx.scene;
  }

  getProject(): Project {
    return this.ctx.history.state;
  }

  setSelection(sel: SceneSelection): void {
    this.ctx.setSelection(sel);
  }

  deleteSelection(): void {
    deleteSelection(this.ctx);
  }

  commit(label: string, mutate: (proj: Project) => Project): void {
    this.ctx.commit(label, mutate);
  }

  // --- Pointer flow --------------------------------------------------------

  onActivate(): void {
    setCanvasCursor(this.getScope(), this.cursor);
  }

  onDeactivate(): void {
    this.del.clear();
  }

  // Shift rides along from the pointer sample: it switches the target from one
  // run of a seam to the whole object, the same modifier construction's Delete
  // uses (#327 / #1030).
  onHover(s: PointerSample, p: { x: number; y: number }): void {
    this.del.handleHover(p, !!s.shiftKey);
  }

  onDown(s: PointerSample, p: { x: number; y: number }): void {
    this.del.handleDown(p, !!s.shiftKey);
  }

  onCancel(): void {
    this.del.clear();
  }

  /** Test hook (#1000): what the next click would delete, or null. */
  getHoverTarget(): string | null {
    return this.del.getHoverTarget();
  }
}
